import React, {PropsWithChildren, useEffect, useMemo, useRef} from "react";
import {useScopedCollection} from "./useScopedCollection";
import {
    collection,
    DocumentData,
    orderBy,
    query,
    QueryConstraint,
    updateDoc,
} from 'firebase/firestore';
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RREmployeePPH, RREmployeePPHs, RREmployeesType, RREmployeeType} from "../app/types/RREmployeeType";
import {firebaseDateToDate} from "../app/tools";
import {setEmployees} from "../features/AppDataSlice";
import {
    RRDepartmentType,
    selectCalendarFirstDate,
    selectCalendarVisibleDays,
    selectCurrentUser, selectDepartment,
    setDepartment
} from "../features/AppStateSlice";
import {convertEmployeeRateDates} from "../app/views/employees/employeeRateAudit";

export const employeesDataConverter = (data: DocumentData[]): RREmployeesType => {
    return data.map(
        (value, listIndex) => {
            const _empl= {
                id: value.id,
                ref: value.ref,
                egn: value.get("egn"),
                name: value.get("name"),
                email: value.get("email"),
                department: value.get("department"),
                phone: value.get("phone"),
                isDeleted: value.get("isDeleted") || false,
                empl_pph: (value.get("empl_pph") || []).map(
                    (value: RREmployeePPH) => convertEmployeeRateDates(value)
                ),
                pph: value.get("pph") || 0,
                supervisor_pph: value.get("supervisor_pph") || 0,
                startDate: firebaseDateToDate(value.get("startDate")) || new Date(),
                leaveDate: firebaseDateToDate(value.get("leaveDate")),
                isSuperAdmin: value.get("isSuperAdmin") || false,
                isAdmin: value.get("isAdmin") || false,
                isEmployee: value.get("isEmployee") || false,
                isMontage: value.get("isMontage") || false,
                isCatering: value.get("isCatering") || false,
                isStore: value.get("isStore") || false,
                isLogistics: value.get("isLogistics") || false,
                canSeePayment: value.get("canSeePayment") || false,
                canSeeLogisticsScheduleReport: value.get("canSeeLogisticsScheduleReport") || false,
                canUseMobileApp: value.get("canUseMobileApp") || false,
                canUseWebApp: value.get("canUseWebApp") || false,
                listIndex_montage: value.get("listIndex_montage") ?? listIndex,
                listIndex_catering: value.get("listIndex_catering") ?? listIndex,
                listIndex_store: value.get("listIndex_store") ?? listIndex,
            } as RREmployeeType;

            if(_empl.empl_pph.length === 0) {
                _empl.empl_pph.push({
                    fromDate: new Date(1999, 0, 1),
                    isApproved: true,
                    pph: _empl.pph,
                    supervisor_pph: _empl.supervisor_pph,
                })
            }

            if (_empl.empl_pph.length === 1 && !_empl.empl_pph[0].isApproved) {
                _empl.empl_pph[0] = {
                    ..._empl.empl_pph[0],
                    isApproved: true,
                };
                _empl.pph = _empl.empl_pph[0].pph;
                _empl.supervisor_pph = _empl.empl_pph[0].supervisor_pph;
            }

            return _empl;
        }
    )
}

export const FirebaseEmployeeLoader: React.FC<PropsWithChildren<{}>> = (props) => {
    const department = useAppSelector(selectDepartment);
    const dispatch = useAppDispatch();
    const firstDate = useAppSelector(selectCalendarFirstDate);
    const visibleDays = useAppSelector(selectCalendarVisibleDays);
    const currentUser = useAppSelector(selectCurrentUser);
    const autoApprovedInitialPPHEmployeeIds = useRef<Set<string>>(new Set<string>());

    const constraints = useMemo<QueryConstraint[]>(
        () => (department ? [orderBy(`listIndex_${department}`, "asc")] : []),
        [department]
    );
    const employeesQuery = useMemo(
        () => query(collection(appFirestore, 'employees'), ...constraints),
        [constraints]
    );
    const {data, loading, error, subscription} = useScopedCollection(employeesQuery, department);
    const employees = useMemo(() => {
        return data ? employeesDataConverter(data.docs) : [];
    }, [data]);

    if (error) {
        console.error(error.message);
    }

    useEffect(() => {
        // Началният отдел се определя само от получен списък, не от междинно празно състояние.
        if (currentUser && data && !loading && !department) {
            const currentEmpl = employees.find(
                e => e.email?.toLocaleLowerCase() === currentUser.email?.toLocaleLowerCase()
            );
            if(!department) {
                let _department: RRDepartmentType = "";
                if(currentEmpl?.isMontage) {
                    _department = "montage";
                } else {
                    if(currentEmpl?.isCatering) {
                        _department = "catering";
                    } else {
                        if (currentEmpl?.isStore) {
                            _department = "store";
                        }  else {
                            if (currentEmpl?.isLogistics) {
                                _department = "logistics";
                            }
                        }
                    }
                }
                if (_department) dispatch(setDepartment(_department));
            }
        }
    }, [currentUser, data, loading, department, employees, dispatch]);

    useEffect(() => {
        if (!data) {
            return;
        }

        data.docs.forEach(docSnapshot => {
            if (autoApprovedInitialPPHEmployeeIds.current.has(docSnapshot.id)) {
                return;
            }

            const employeePPHs = docSnapshot.get("empl_pph") as RREmployeePPHs | undefined;
            if (!employeePPHs || employeePPHs.length !== 1 || employeePPHs[0].isApproved) {
                return;
            }

            const approvedInitialPPH = {
                ...employeePPHs[0],
                isApproved: true,
            };
            autoApprovedInitialPPHEmployeeIds.current.add(docSnapshot.id);
            updateDoc(docSnapshot.ref, {
                empl_pph: [approvedInitialPPH],
                pph: approvedInitialPPH.pph || 0,
                supervisor_pph: approvedInitialPPH.supervisor_pph || 0,
            }).catch(error => {
                autoApprovedInitialPPHEmployeeIds.current.delete(docSnapshot.id);
                console.error(error);
            });
        });
    }, [data]);

    useEffect(() => {
        const lastDate = new Date(firstDate);
        lastDate.setDate(lastDate.getDate() + visibleDays);
        dispatch(setEmployees({
            data: {loading, error: error?.message, data: employees},
            firstDay: firstDate, lastDay: lastDate,
        }));
    }, [dispatch, firstDate, visibleDays, loading, error, employees]);

    return <>{subscription}{props.children}</>;
}
